import { useFormContext } from "react-hook-form";
import { FormControl, FormField, FormItem, FormLabel } from "@/components/ui/form";
import { Switch } from "@/components/ui/switch";
import { TextInput } from "@/components/ui/textInput";
import type { QuoteFormSchema } from "@/lib/schemas/createUpdateQuoteSchema";

type NumberFieldProps = {
  name:
    | "costBreakdown.supervisorQuantity"
    | "costBreakdown.supervisorHours"
    | "costBreakdown.supervisorRate"
    | "costBreakdown.technicianQuantity"
    | "costBreakdown.technicianHours"
    | "costBreakdown.technicianRate"
    | "costBreakdown.equipment"
    | "costBreakdown.other";
  label: string;
  step?: number;
};

function NumberField({ name, label, step = 0.01 }: NumberFieldProps) {
  const { register } = useFormContext<QuoteFormSchema>();

  return (
    <div className="flex flex-col gap-1">
      <label htmlFor={name} className="font-semibold text-gray-700 text-sm">
        {label}
      </label>
      <TextInput
        id={name}
        type="number"
        min={0}
        step={step}
        {...register(name, { valueAsNumber: true })}
        startAdornment={
          name.includes("Rate") || ["costBreakdown.equipment", "costBreakdown.other"].includes(name) ? (
            <span className="text-gray-500">$</span>
          ) : undefined
        }
      />
    </div>
  );
}

export default function QuoteCostBreakdown() {
  const { control, watch } = useFormContext<QuoteFormSchema>();
  const costBreakdown = watch("costBreakdown");
  const supervisorTotal =
    costBreakdown.supervisorQuantity * costBreakdown.supervisorHours * costBreakdown.supervisorRate;
  const technicianTotal =
    costBreakdown.technicianQuantity * costBreakdown.technicianHours * costBreakdown.technicianRate;
  const laborTotal = supervisorTotal + technicianTotal;
  const enabled = watch("costBreakdownEnabled");

  return (
    <div className="rounded-lg border border-gray-200 bg-gray-50 p-4 sm:col-span-2 xl:col-span-3">
      <FormField
        control={control}
        name="costBreakdownEnabled"
        render={({ field }) => (
          <FormItem className="flex items-center justify-between gap-3">
            <div>
              <FormLabel className="font-semibold text-gray-900 text-sm md:text-base">
                Labor, Equipment, Materials, Other Breakdown
              </FormLabel>
              <p className="mt-1 text-gray-600 text-xs">Show the lump sum quote cost breakdown.</p>
            </div>
            <FormControl>
              <Switch checked={!!field.value} onCheckedChange={field.onChange} aria-label="Show cost breakdown" />
            </FormControl>
          </FormItem>
        )}
      />

      {enabled && (
        <div className="mt-4 flex flex-col gap-5">
          <div>
            <h3 className="mb-3 font-semibold text-gray-900">Labor</h3>
            <div className="overflow-x-auto rounded-md border border-gray-200 bg-white">
              <table className="w-full min-w-[680px] border-collapse">
                <thead>
                  <tr className="bg-gray-50 text-left text-gray-600 text-xs uppercase">
                    <th className="p-3">Labor Type</th>
                    <th className="p-3">Quantity</th>
                    <th className="p-3">Hours per Worker</th>
                    <th className="p-3">Hourly Rate</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-gray-100 border-t">
                    <td className="p-3 font-semibold">Supervisor</td>
                    <td className="p-3">
                      <NumberField name="costBreakdown.supervisorQuantity" label="Supervisor quantity" step={1} />
                    </td>
                    <td className="p-3">
                      <NumberField name="costBreakdown.supervisorHours" label="Supervisor hours" step={0.25} />
                    </td>
                    <td className="p-3">
                      <NumberField name="costBreakdown.supervisorRate" label="Supervisor rate" />
                    </td>
                  </tr>
                  <tr className="border-gray-100 border-t">
                    <td className="p-3 font-semibold">Technician</td>
                    <td className="p-3">
                      <NumberField name="costBreakdown.technicianQuantity" label="Technician quantity" step={1} />
                    </td>
                    <td className="p-3">
                      <NumberField name="costBreakdown.technicianHours" label="Technician hours" step={0.25} />
                    </td>
                    <td className="p-3">
                      <NumberField name="costBreakdown.technicianRate" label="Technician rate" />
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
          <div className="flex items-end justify-between gap-4">
            <div>
              <h3 className="mb-3 font-semibold text-gray-900">Additional Costs</h3>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <NumberField name="costBreakdown.equipment" label="Equipment" />
                <NumberField name="costBreakdown.other" label="Other" />
              </div>
            </div>
            <div className="rounded-md bg-white px-4 py-3 text-right">
              <p className="font-semibold text-gray-600 text-sm">Total Labor</p>
              <p className="font-bold text-gray-900 text-lg">${laborTotal.toFixed(2)}</p>
            </div>
          </div>
          <FormField
            control={control}
            name="costBreakdown.workersCompEnabled"
            render={({ field }) => (
              <FormItem className="flex items-center justify-between gap-3 rounded-md border border-gray-200 bg-white p-3">
                <div>
                  <FormLabel className="font-semibold text-gray-900 text-sm">
                    Workers&apos; Compensation Reduction
                  </FormLabel>
                  <p className="mt-1 text-gray-600 text-xs">Enable the 4% reduction on gross labor.</p>
                </div>
                <FormControl>
                  <Switch
                    checked={!!field.value}
                    onCheckedChange={field.onChange}
                    aria-label="Workers compensation reduction"
                  />
                </FormControl>
              </FormItem>
            )}
          />
        </div>
      )}
    </div>
  );
}
